import React from 'react';

import BranchwiseUnbilledTable from '../../unbilled/components/BranchwiseUnbilledTable';
import BranchCnDetailsTable from '../../unbilled/components/BranchCnDetailsTable';
import { BranchwiseUnbilledRow, BranchCnDetailsRow } from '../../unbilled/api/api';

import {
    fetchKeyAccountUnbilledSummary,
    fetchKeyAccountCnDetails,
    downloadKeyAccountCnDetails,
    SnapshotRef,
} from '../api';
import SummaryStrip from '../components/SummaryStrip';
import { ConversionType } from '../components/UnitToggle';
import { formatCurrency, formatSnapshotDate } from '../components/format';

const BRANCH_SORT_MAP: Record<string, string> = {
    ou_code: 'ou_code',
    Branch: 'branch',
    TotalUnbilled: 'total_unbilled',
    InTransit: 'in_transit',
    InStock: 'in_stock',
    DeliveredLessThanEqual10: 'delivered_lte_10',
    DeliveredGreaterThan10: 'delivered_gt_10',
    PodReceivedLessThanEqual10: 'pod_received_lte_10',
    PodReceivedGreaterThan10: 'pod_received_gt_10',
};

interface Props {
    customerCode: string;
    customerName: string;
    conversionType: ConversionType;
    onConversionChange: (type: ConversionType) => void;
}

const UnbilledTab: React.FC<Props> = ({
    customerCode,
    customerName,
    conversionType,
    onConversionChange,
}) => {
    const [snapshot, setSnapshot] = React.useState<SnapshotRef | null>(null);
    const [error, setError] = React.useState<string>('');

    // branch-wise summary (rows[0] = customer TOTAL)
    const [rows, setRows] = React.useState<BranchwiseUnbilledRow[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [totalItems, setTotalItems] = React.useState<number>(0);
    const [page, setPage] = React.useState<number>(1);
    const [pageSize, setPageSize] = React.useState<number>(20);
    const [sortBy, setSortBy] = React.useState<keyof BranchwiseUnbilledRow>('TotalUnbilled');
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');
    const [search, setSearch] = React.useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');
    const [hasLoadedOnce, setHasLoadedOnce] = React.useState<boolean>(false);

    // CN drill-down
    const [selectedBranch, setSelectedBranch] = React.useState<string | null>(null);
    const [selectedBranchName, setSelectedBranchName] = React.useState<string | null>(null);
    const [cnRows, setCnRows] = React.useState<BranchCnDetailsRow[]>([]);
    const [cnLoading, setCnLoading] = React.useState<boolean>(false);
    const [cnTotalItems, setCnTotalItems] = React.useState<number>(0);
    const [cnPage, setCnPage] = React.useState<number>(1);
    const [cnPageSize, setCnPageSize] = React.useState<number>(20);
    const [cnSortBy, setCnSortBy] = React.useState<string>('cn_date');
    const [cnSortOrder, setCnSortOrder] = React.useState<'asc' | 'desc'>('desc');
    const [cnSearch, setCnSearch] = React.useState<string>('');
    const [debouncedCnSearch, setDebouncedCnSearch] = React.useState<string>('');
    const [cnStatus, setCnStatus] = React.useState<string | null>(null);
    const [downloadingExcel, setDownloadingExcel] = React.useState<boolean>(false);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedCnSearch(cnSearch.trim());
            setCnPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [cnSearch]);

    // branch-wise summary
    React.useEffect(() => {
        if (!customerCode) return;

        setLoading(true);
        setError('');

        fetchKeyAccountUnbilledSummary({
            customer_code: customerCode,
            search: debouncedSearch || undefined,
            page,
            per_page: pageSize,
            sort_by: BRANCH_SORT_MAP[sortBy] || 'total_unbilled',
            sort_order: sortOrder,
        })
            .then((resp) => {
                setSnapshot(resp.snapshot);
                setRows(resp.rows);
                setTotalItems(resp.total_items);
            })
            .catch((err: unknown) => {
                const isForbidden = (err as any)?.status === 403;
                setError(
                    isForbidden
                        ? 'You are not allowed to access this resource.'
                        : err instanceof Error
                            ? err.message
                            : 'Failed to fetch unbilled data'
                );
                setRows([]);
                setTotalItems(0);
            })
            .finally(() => {
                setLoading(false);
                setHasLoadedOnce(true);
            });
    }, [customerCode, debouncedSearch, page, pageSize, sortBy, sortOrder]);

    // CN details
    React.useEffect(() => {
        if (!customerCode || !selectedBranch) return;

        setCnLoading(true);

        fetchKeyAccountCnDetails({
            customer_code: customerCode,
            ou_code: selectedBranch,
            status: cnStatus || undefined,
            search: debouncedCnSearch || undefined,
            page: cnPage,
            per_page: cnPageSize,
            sort_by: cnSortBy,
            sort_order: cnSortOrder,
        })
            .then((resp) => {
                setCnRows(resp.rows);
                setCnTotalItems(resp.total_items);
            })
            .catch(() => {
                setCnRows([]);
                setCnTotalItems(0);
            })
            .finally(() => setCnLoading(false));
    }, [customerCode, selectedBranch, cnStatus, debouncedCnSearch, cnPage, cnPageSize, cnSortBy, cnSortOrder]);

    const handleDownloadCnExcel = async () => {
        if (!selectedBranch) return;
        try {
            setDownloadingExcel(true);
            const blob = await downloadKeyAccountCnDetails({
                customer_code: customerCode,
                ou_code: selectedBranch,
                status: cnStatus || undefined,
                search: debouncedCnSearch || undefined,
                sort_by: cnSortBy,
                sort_order: cnSortOrder,
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            const safeCustomer = customerName.replace(/[^\w\-. ]/g, '_');
            const safeBranch = (selectedBranchName || selectedBranch).replace(/[^\w\-. ]/g, '_');
            a.download = `Unbilled_CN_Details_${safeCustomer}_${safeBranch}.xlsx`;

            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('CN Excel download failed', err);
        } finally {
            setDownloadingExcel(false);
        }
    };

    const totalsRow = rows.length > 0 && rows[0].Branch === 'TOTAL' ? rows[0] : null;
    const unitLabel = conversionType === 'INR' ? '' : ` (${conversionType})`;

    if (error) {
        return (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{error}</p>
            </div>
        );
    }

    if (hasLoadedOnce && !snapshot) {
        return (
            <div className="text-center py-12 text-gray-500">
                No unbilled snapshot available yet.
            </div>
        );
    }

    if (selectedBranch) {
        return (
            <div className="flex flex-col" style={{ minHeight: '60vh' }}>
                <BranchCnDetailsTable
                    data={cnRows}
                    loading={cnLoading}
                    page={cnPage}
                    pageSize={cnPageSize}
                    totalItems={cnTotalItems}
                    sortBy={cnSortBy}
                    sortOrder={cnSortOrder}
                    search={cnSearch}
                    status={cnStatus}
                    onSearchChange={setCnSearch}
                    onStatusChange={(s) => {
                        setCnPage(1);
                        setCnStatus(s);
                    }}
                    onSortChange={(col) => {
                        setCnPage(1);
                        if (cnSortBy === col) {
                            setCnSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'));
                        } else {
                            setCnSortBy(col);
                            setCnSortOrder('asc');
                        }
                    }}
                    onPageChange={setCnPage}
                    onPageSizeChange={(s) => {
                        setCnPage(1);
                        setCnPageSize(s);
                    }}
                    onBack={() => {
                        setSelectedBranch(null);
                        setSelectedBranchName(null);
                        setCnSearch('');
                        setCnStatus(null);
                    }}
                    customerName={customerName}
                    branchName={selectedBranchName}
                    onDownloadExcel={handleDownloadCnExcel}
                    downloadingExcel={downloadingExcel}
                />
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <SummaryStrip
                title="Customer Unbilled Summary"
                asOf={formatSnapshotDate(snapshot)}
                loading={loading && !totalsRow}
                items={
                    totalsRow
                        ? [
                            { label: `Total Unbilled${unitLabel}`, value: formatCurrency(totalsRow.TotalUnbilled, conversionType) },
                            { label: `In Transit${unitLabel}`, value: formatCurrency(totalsRow.InTransit, conversionType) },
                            { label: `In Stock${unitLabel}`, value: formatCurrency(totalsRow.InStock, conversionType) },
                            { label: `Delivered <= 10${unitLabel}`, value: formatCurrency(totalsRow.DeliveredLessThanEqual10, conversionType) },
                            { label: `Delivered > 10${unitLabel}`, value: formatCurrency(totalsRow.DeliveredGreaterThan10, conversionType), valueClassName: totalsRow.DeliveredGreaterThan10 > 0 ? 'text-red-700' : '' },
                            { label: `POD Received <= 10${unitLabel}`, value: formatCurrency(totalsRow.PodReceivedLessThanEqual10, conversionType) },
                            { label: `POD Received > 10${unitLabel}`, value: formatCurrency(totalsRow.PodReceivedGreaterThan10, conversionType), valueClassName: totalsRow.PodReceivedGreaterThan10 > 0 ? 'text-red-700' : '' },
                        ]
                        : []
                }
            />

            <div className="flex flex-col" style={{ minHeight: '50vh' }}>
                <BranchwiseUnbilledTable
                    data={rows}
                    loading={loading}
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    search={search}
                    onSearchChange={setSearch}
                    onSortChange={(col) => {
                        setPage(1);
                        if (sortBy === col) {
                            setSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'));
                        } else {
                            setSortBy(col);
                            setSortOrder('asc');
                        }
                    }}
                    onPageChange={setPage}
                    onPageSizeChange={(s) => {
                        setPage(1);
                        setPageSize(s);
                    }}
                    onBranchClick={(ou, name) => {
                        setSelectedBranch(ou);
                        setSelectedBranchName(name);
                        setCnPage(1);
                    }}
                    conversionType={conversionType}
                    onConversionChange={onConversionChange}
                />
            </div>
        </div>
    );
};

export default UnbilledTab;
